import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import CompactDataTable from 'src/components/CompactDataTable';
import InfoAlert from 'src/components/InfoAlert';
import ListActionBar from 'src/components/ListActionBar';
import TableActionButton from 'src/components/TableActionButton';
import { pagesText } from 'src/constants/pagesText';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { EXPANDED_ROWS_LIMIT } from 'src/constants/table';
import { useExportPolicyDevices, useInfinitePolicyDevices } from 'src/hooks/environmentWdacPolicy';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { DEVICES_PREVIOUS_PATH } from 'src/pages/devices/deviceDetails/constant';
import { csvExportFileName, handleCsvFileDownload } from 'src/utils/download.util';
import { toHasPolicy } from 'src/utils/enums/uiOnly/policyDeviceMembership';
import { DEFAULT_DEVICE_FILTERS, DEVICE_COLUMNS, DEVICE_FILTER_OPTIONS } from './constants';

const ALERT_SX = { width: '100%', py: 0.25 };

const PolicyDevices = () => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_DEVICE_FILTERS) ?? DEFAULT_DEVICE_FILTERS,
  );

  useFilterParams(filters);

  const hasPolicy = toHasPolicy(filters.membership);

  const devicesQuery = useInfinitePolicyDevices({
    envPolicyId: policyId,
    hasPolicy,
    search: searchValue,
    limit: EXPANDED_ROWS_LIMIT,
  });

  const rows = devicesQuery.data?.pages?.flatMap(page => page.results || []) || [];
  const totalCount = devicesQuery.data?.pages?.[0]?.totalCount;

  const [sentryRef] = useInfiniteScroll({
    loading: devicesQuery.isFetchingNextPage,
    hasNextPage: !!devicesQuery.hasNextPage,
    onLoadMore: devicesQuery.fetchNextPage,
    rootMargin: '0px 0px 400px 0px',
  });

  // The export follows the list's side of the policy and its search, but not its paging.
  const exportMutation = useExportPolicyDevices();
  const handleExport = async () => {
    try {
      const csv = await exportMutation.mutateAsync({
        envPolicyId: policyId,
        hasPolicy,
        search: searchValue,
      });
      handleCsvFileDownload(
        csv,
        csvExportFileName(
          hasPolicy ? 'policy_devices_with_policy' : 'policy_devices_without_policy',
        ),
      );
    } catch {
      enqueueSnackbar(pagesText.somethingWentWrong, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  // The device page's back button returns here, as it does from the other device lists.
  const openDevice = row => {
    sessionStorage.setItem(
      DEVICES_PREVIOUS_PATH,
      `${window.location.pathname}${window.location.search}`,
    );
    navigate(`${PORTAL_ROUTE}/devices/${row.deviceId}/general`);
  };

  return (
    <Box>
      <ListActionBar
        onSearch={setSearchValue}
        searchPlaceholder="Search by device name..."
        filters={filters}
        onFilterChange={setFilters}
        filterOptions={DEVICE_FILTER_OPTIONS}
        filterChips
        filterButtonCount={totalCount}
        contentArea={
          <InfoAlert sx={ALERT_SX}>
            <Typography variant="caption">
              With Policy lists devices that have reported any version of this policy at any point,
              whether or not it is still active on them, and Version is the one each reported most
              recently. Without Policy lists devices that have never reported it. Archived and
              revoked devices are excluded from both.
            </Typography>
          </InfoAlert>
        }
        contentAreaContainerProps={{ sx: { mt: 0.5 } }}
        actionArea={
          <TableActionButton
            variant="outlined"
            onClick={handleExport}
            disabled={exportMutation.isLoading}
            sx={{ px: 1.5 }}>
            {exportMutation.isLoading ? 'Exporting...' : 'Export'}
          </TableActionButton>
        }
      />
      <CompactDataTable
        columns={DEVICE_COLUMNS}
        rows={rows.map(row => ({ ...row, id: row.deviceId }))}
        isLoading={devicesQuery.isLoading}
        isFetching={devicesQuery.isFetching}
        lastElementRef={sentryRef}
        searchValue={searchValue}
        onRowClick={openDevice}
      />
    </Box>
  );
};

export default PolicyDevices;
